"use client"
import { useQualityChanges } from "@/hooks/use-quality-presentations"


import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import {
  conversationQualityQueryKey,
  findImprovementsQueryKey,
  findImprovementsStatusQueryKey,
  qualityDigestSettingsQueryKey,
  qualitySuggestionsQueryKey,
} from "@/hooks/queries/conversation-quality"
import { UNIFIED_INBOX_QUERY_KEY } from "@/hooks/queries/unified-inbox"

import { formatReportWeek } from "@/lib/conversation-quality/report-week"
import type { FindNowReport, SuggestionKind } from "@/lib/conversation-quality/suggestions/schema"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  setQualityEnabledAction,
  type SetQualityEnabledInput,
} from "@/server-actions/conversation-quality-access"
import {
  correctSessionOutcomeAction,
  type CorrectSessionOutcomeInput,
} from "@/server-actions/conversation-quality-review"
import {
  acceptKbSuggestionAction,
  acceptPromptSuggestionAction,
  rejectSuggestionAction,
  type AcceptKbSuggestionInput,
  type AcceptPromptSuggestionInput,
  type SuggestionIdInput,
} from "@/server-actions/conversation-quality-suggestions"
import { undoQualityChangeAction } from "@/server-actions/conversation-quality-changes"
import { findImprovementsNowAction, type QualitySuggestionsInput } from "@/server-actions/conversation-quality"
import {
  sendTestQualityDigestAction,
  updateQualityDigestSettingsAction,
  type UpdateQualityDigestInput,
} from "@/server-actions/conversation-quality-digest"
import type { TestDigestResult } from "@/lib/conversation-quality/suggestions/digest"
import { useTranslations } from "next-intl"


type SetErrors = (errors: Record<string, unknown>) => void

export type CorrectSessionOutcomeMutationInput = CorrectSessionOutcomeInput & {
  setErrors?: SetErrors
}

/**
 * The operator's correction of a session verdict.
 *
 * `setErrors` carries the dialog's Formik: the fact-dependent checks — a category on an
 * escalation, an outcome for the person who answered — can only be made against the stored
 * row, and they belong beside the field that is missing rather than in a toast.
 */
export function useCorrectSessionOutcomeMutation(options?: {
  onCorrected?: () => void | Promise<void>
}) {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsConversationQuality")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ setErrors, ...input }: CorrectSessionOutcomeMutationInput) => {
      const result = await withQueryHandling(correctSessionOutcomeAction(input), {
        loading: tUi("savingTheCorrection"),
        success: tUi("outcomeCorrected"),
        setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      // By prefix: the badge lives on the thread, and the analytics count what just changed.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: UNIFIED_INBOX_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: conversationQualityQueryKey }),
      ])
      await options?.onCorrected?.()
    },
  })
}

export function useAcceptKbSuggestionMutation(options?: { onAccepted?: () => void | Promise<void> }) {
  const tUi = useTranslations("Feedback.MutationsConversationQuality")

  return useSuggestionDecision<AcceptKbSuggestionInput>(
    acceptKbSuggestionAction,
    { loading: tUi("addingToTheKnowledgeBase"), success: tUi("addedToTheKnowledgeBase") },
    options?.onAccepted
  )
}

function getAcceptPromptMessages(t: ReturnType<typeof useTranslations<"Feedback.MutationsConversationQuality">>) {
  return {loading: t("savingAgentInstructions"), success: t("agentInstructionsUpdated")}
}

export function useAcceptPromptSuggestionMutation(options?: {
  onAccepted?: () => void | Promise<void>
}) {
  return useSuggestionDecision<AcceptPromptSuggestionInput>(
    acceptPromptSuggestionAction,
    getAcceptPromptMessages(useTranslations("Feedback.MutationsConversationQuality")),
    options?.onAccepted
  )
}

/** A 409 an apply from the list meets: the change needs its review, whose toast says why. */
class NeedsReview extends Error {}

/**
 * Applies a waiting instruction change as drafted, from the list. A conflict — its line or the instructions moved, or
 * the agent has no instructions of its own — resolves to `review` for the caller to open the review.
 */
export function useApplySuggestionMutation() {
  const tUi = useTranslations("Feedback.MutationsConversationQuality")
  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: AcceptPromptSuggestionInput): Promise<"applied" | "review"> => {
      const applied = acceptPromptSuggestionAction(input).then((result) => {
        if ("status" in result && result.status === 409) throw new NeedsReview(result.errorMessage)
        return result
      })
      try {
        await withQueryHandling(applied, getAcceptPromptMessages(tUi))
        return "applied"
      } catch (error) {
        if (error instanceof NeedsReview) return "review"
        throw error
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qualitySuggestionsQueryKey }),
  })
}

export function useRejectSuggestionMutation(
  kind: SuggestionKind,
  options?: { onRejected?: () => void | Promise<void> }
) {
  const { refusalNote } = useQualityChanges()

  const tUi = useTranslations("Feedback.MutationsConversationQuality")

  return useSuggestionDecision<SuggestionIdInput>(
    rejectSuggestionAction,
    { loading: tUi("savingYourDecision"), success: refusalNote(kind, "no") },
    options?.onRejected
  )
}

export function useUndoQualityChangeMutation(options?: { onUndone?: () => void | Promise<void> }) {
  const tUi = useTranslations("Feedback.MutationsConversationQuality")

  return useSuggestionDecision<SuggestionIdInput>(
    undoQualityChangeAction,
    { loading: tUi("undoingTheChange"), success: tUi("changeUndone") },
    options?.onUndone
  )
}

/** The whole page follows the switch, and so does the badge on every thread. */
export function useSetQualityEnabledMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SetQualityEnabledInput) => {
      const result = await withQueryHandling(setQualityEnabledAction(input), {
        loading: input.enabled ? "Turning Conversation Quality on..." : "Turning Conversation Quality off...",
        success: input.enabled ? "Conversation Quality is on" : "Conversation Quality is off",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: conversationQualityQueryKey }),
        queryClient.invalidateQueries({ queryKey: UNIFIED_INBOX_QUERY_KEY }),
      ]),
  })
}

export function useUpdateQualityDigestSettingsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsConversationQuality")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UpdateQualityDigestInput) => {
      const result = await withQueryHandling(updateQualityDigestSettingsAction(input), {
        loading: tUi("savingTheDigestSettings"),
        success: tUi("digestSettingsSaved"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: (data) => queryClient.setQueryData(qualityDigestSettingsQueryKey, data),
    // Learning & improvements shows whether the digest is on.
    onSettled: () => queryClient.invalidateQueries({ queryKey: qualitySuggestionsQueryKey }),
  })
}

/**
 * A started press reads as running at once, so its end is seen however soon it comes; one refused after all — another
 * run started, or nothing is ready — says why instead.
 */
export function useFindImprovementsNowMutation(timezone: string) {
  const { findNowRefusal } = useQualityChanges()

  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: QualitySuggestionsInput) => {
      const result = await withQueryHandling(findImprovementsNowAction(input), {
        showLoadingToast: false,
        showSuccessToast: false,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      if (!result.started) toast.info(findNowRefusal(result.current, timezone))
      return result
    },
    onSuccess: (result, input) => {
      if (!result.started) return
      queryClient.setQueryData<FindNowReport>(
        findImprovementsStatusQueryKey(input),
        (report) => report && { ...report, current: { state: "running" } }
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: findImprovementsQueryKey }),
  })
}

export function useSendTestQualityDigestMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsConversationQuality")

  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(sendTestQualityDigestAction({}), {
        loading: tUi("sendingATestDigest"),
        showSuccessToast: false,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: (result) => {
      const { tone, message } = describeTestDigest(result, tUi)
      toast[tone](message)
    },
  })
}

function describeTestDigest(result: TestDigestResult, tUi: ReturnType<typeof useTranslations<"Feedback.MutationsConversationQuality">>): { tone: "success" | "warning"; message: string } {
  switch (result.outcome) {
    case "sent": {
      const failed = result.deliveries.filter((delivery) => delivery.status !== "delivered")
      const channels = result.deliveries.map((delivery) => `${delivery.channelName}: ${delivery.status}`).join(", ")
      return {
        tone: failed.length === 0 ? "success" : "warning",
        message: tUi("testDigestForValue0SentValue1", { value0: String(formatReportWeek(result.periodStart)), value1: String(channels) }),
      }
    }
    case "disabled":
      return { tone: "warning", message: tUi("theDigestIsSwitchedOffSoNoTest") }
    case "nothing_to_report":
      return { tone: "warning", message: tUi("noRecentWeekHadConversationsToReport") }
    case "no_channel":
      return { tone: "warning", message: tUi("noChannelCanDeliverTheDigestSelectA") }
    default: {
      const exhaustive: never = result
      return exhaustive
    }
  }
}

/**
 * One decision on a suggestion. `setErrors` carries a dialog's Formik. The queue refreshes once the
 * decision settles, not only on success: a suggestion somebody else just decided has to leave it too.
 */
function useSuggestionDecision<Input>(
  decide: (input: Input) => Promise<unknown>,
  messages: { loading: string; success: string },
  onDecided?: () => void | Promise<void>
) {
  const withQueryHandling = useTranslatedQueryHandling()

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ input, setErrors }: { input: Input; setErrors?: SetErrors }) => {
      await withQueryHandling(decide(input), { ...messages, setErrors })
    },
    onSuccess: async () => {
      await onDecided?.()
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: qualitySuggestionsQueryKey }),
  })
}
